import { toRem, toRems } from '../../../../helpers/theme'
import { normalizeDimension } from '../../../../helpers/utils'

const style = ({ theme, labelAlignLeft, size, variant, fieldWidth, inputWidth, disabled, focused, error }) => {
    const config = {
        primary: {
            input: {
                fontSize: 16,
                fontWeight: theme.typography.fontWeight.regular,
                borderRadius: 100,
                boxShadow: {
                    default: 'none',
                    focus: theme.shadows.input.focus
                },
                arrow: 14,
                small: {
                    padding: [5, 36, 5, 22],
                    height: 40
                },
                normal: {
                    padding: [10, 36, 10, 22],
                    height: 48
                },
                large: {
                    padding: [10, 36, 10, 22],
                    height: 62
                }
            }
        },
        secondary: {
            input: {
                fontSize: 16,
                fontWeight: theme.typography.fontWeight.regular,
                borderRadius: 100,
                boxShadow: {
                    default: 'none',
                    focus: theme.shadows.input.focus
                },
                arrow: 14,
                small: {
                    padding: [5, 36, 5, 22],
                    height: 40
                },
                normal: {
                    padding: [10, 36, 10, 22],
                    height: 48
                },
                large: {
                    padding: [10, 36, 10, 22],
                    height: 62
                }
            }
        },
        tertiary: {
            input: {
                fontSize: 16,
                fontWeight: theme.typography.fontWeight.bold,
                borderRadius: 8,
                boxShadow: {
                    default: theme.shadows.subtle,
                    focus: theme.shadows.subtle
                },
                arrow: 14,
                small: {
                    padding: [5, 46, 5, 22],
                    height: 40
                },
                normal: {
                    padding: [6, 46, 6, 22],
                    height: 46
                },
                large: {
                    padding: [10, 46, 10, 22],
                    height: 62
                }
            }
        }
    }

    return {
        position: 'relative',
        margin: 0,
        padding: 0,
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        ...(fieldWidth && {
            maxWidth: normalizeDimension(fieldWidth, true)
        }),
        [theme.breakpoints.up('md')]: {
            ...(labelAlignLeft && {
                flexDirection: 'row',
                alignItems: 'flex-start'
            })
        },
        '& .inputContainer': {
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            alignItems: 'flex-start',
            width: '100%',
            '&_inner': {
                display: 'flex',
                flexDirection: 'row',
                flex: 1,
                width: '100%'
            }
        },
        '& .inputWrap': {
            position: 'relative',
            cursor: 'pointer',
            width: '100%',
            display: 'inline-flex',
            boxSizing: 'border-box',
            alignItems: 'center',
            background: error
                ? theme.colors.form.error.background
                : theme.colors.form.input[variant]?.background?.default,
            border: '1px solid',
            borderColor: error ? theme.colors.form.error.border : theme.colors.form.input[variant]?.border?.default,
            borderRadius: toRem(config[variant]?.input.borderRadius),
            boxShadow: config[variant]?.input.boxShadow.default,
            transitionDuration: theme.transitions.input.text.default.duration,
            transitionTimingFunction: theme.transitions.input.text.default.timingFunction,
            transitionDelay: theme.transitions.input.text.default.delay,
            transitionProperty: 'color, background, border-color, box-shadow',
            ...(inputWidth && {
                maxWidth: normalizeDimension(inputWidth, true)
            }),
            ...(focused && {
                outline: 0,
                background: error
                    ? theme.colors.form.error.background
                    : theme.colors.form.input[variant]?.background?.focus,
                borderColor: error ? theme.colors.form.error.border : theme.colors.form.input[variant]?.border?.focus,
                boxShadow: error ? 'none' : config[variant]?.input.boxShadow.focus
            }),
            ...(disabled && {
                cursor: 'not-allowed'
            }),
            [theme.breakpoints.up('md')]: {
                ...(labelAlignLeft && {
                    flex: 1
                })
            },
            '&:hover': {
                ...(!disabled && {
                    borderColor: error
                        ? theme.colors.form.error.border
                        : theme.colors.form.input[variant]?.border?.hover
                })
            },
            select: {
                display: 'block',
                color: error ? theme.colors.form.error.text : theme.colors.form.input[variant]?.text?.default,
                padding: toRems(config[variant]?.input[size]?.padding),
                border: 0,
                borderRadius: toRem(config[variant]?.input.borderRadius),
                boxSizing: 'border-box',
                background: error ? theme.colors.form.error.background : 'transparent',
                height: toRem(config[variant]?.input[size]?.height),
                margin: 0,
                WebkitTapHighlightColor: 'transparent',
                minWidth: 0,
                width: '100%',
                fontSize: toRem(config[variant]?.input.fontSize),
                fontWeight: config[variant]?.input.fontWeight,
                lineHeight: 1.4,
                appearance: 'none',
                WebkitAppearance: 'none',
                MozAppearance: 'none',
                '&:focus': {
                    outline: 0
                },
                '&:disabled': {
                    cursor: 'not-allowed',
                    color: theme.colors.form.input[variant]?.text?.disabled
                },
                '&:invalid': {
                    boxShadow: 'none'
                }
            },
            '& .selectArrow': {
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                position: 'absolute',
                right: toRem(15),
                top: '52%',
                zIndex: theme.zIndex.over,
                fontSize: toRem(config[variant]?.input.arrow),
                marginTop: toRem(-config[variant]?.input.arrow / 2), // lets keep transform available for other things
                color: error ? theme.colors.form.error.text : theme.colors.form.input[variant]?.text?.default,
                pointerEvents: 'none',
                ...(disabled && {
                    color: theme.colors.form.input[variant]?.text?.disabled
                }),
                svg: {
                    path: {
                        fill: 'currentColor'
                    }
                }
            }
        }
    }
}

export default style
